import { Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Skeleton } from "@internal/ui"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"

/**
 * The gradient plan card, with the title every state shares.
 *
 * The heading is outside the branch on purpose: loading, failing and having no plan are all
 * answers to "what is my Carrot Plan", so the card keeps its identity instead of the page
 * appearing and disappearing under the member.
 */
export function PlanCard({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Tile variant="primary" color="primary">
      <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
        <Typography variant="h2" component="h2">
          <FormattedMessage defaultMessage="Your Carrot Plan" />
        </Typography>
        {children}
      </Stack>
    </Tile>
  )
}

const SKELETON_STEP_COUNT = 5

const SKELETON_TITLE_WIDTHS = ["62%", "48%", "70%", "54%", "58%"]

function PlanStepTileSkeleton({ titleWidth }: { titleWidth: string }): JSX.Element {
  const theme = useTheme()
  return (
    <Tile
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.sm} solid ${theme.palette.divider}`,
        boxShadow: "none"
      })}
    >
      <Stack spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <Skeleton width={88} height={18} />
        <Skeleton width={titleWidth} height={24} />
        <Skeleton width="100%" height={14} />
        <Skeleton width="76%" height={14} />
        <Skeleton
          width={132}
          height={36}
          sx={{
            marginBlockStart: theme.spacing(theme.tokens.spacing.xs),
            borderRadius: theme.tokens.borderRadius.md
          }}
        />
      </Stack>
    </Tile>
  )
}

export function PlanCardSkeleton(): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  return (
    <PlanCard>
      <Stack
        role="status"
        aria-label={intl.formatMessage({ defaultMessage: "Loading your Carrot Plan" })}
        spacing={theme.spacing(theme.tokens.spacing.lg)}
      >
        <Skeleton width="84%" height={16} />
        <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
          {Array.from({ length: SKELETON_STEP_COUNT }, (_unused, index) => (
            <PlanStepTileSkeleton
              key={index}
              titleWidth={SKELETON_TITLE_WIDTHS[index % SKELETON_TITLE_WIDTHS.length]}
            />
          ))}
        </Stack>
      </Stack>
    </PlanCard>
  )
}
